import { useState, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import dayjs from 'dayjs';
import DeleteCallModal from '../Calls/DeleteCallModal';

import AuthContext from '../../context/authContext';
import CallContext from '../../context/callContext';
import LockIconWrapper from '../components/LockIconWrapper';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

export default function Calls() {
  const authCtx = useContext(AuthContext);
  const callCtx = useContext(CallContext);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const canDelete = authCtx.checkRightAny('call:delete');
  const advancedMode = authCtx.checkRight('call:special:advanced');

  // Set shouldFetchCalls to true when component mounts
  useEffect(() => {
    callCtx.setShouldFetchCalls(true);

    // Set shouldFetchCalls to false when component unmounts
    return () => {
      callCtx.setShouldFetchCalls(false);
    };
  }, [callCtx]);

  // Auto-select agent if only one is available
  useEffect(() => {
    if (callCtx.availableAgents.length === 1 && !callCtx.selectedAgent) {
      callCtx.setSelectedAgent(callCtx.availableAgents[0]);
    }
  }, [callCtx.availableAgents, callCtx.selectedAgent]);

  // Handle initial loading state
  useEffect(() => {
    if (callCtx.selectedAgent && callCtx.shouldFetchCalls) {
      // Set a timeout to show loading state for at least 1 second
      const timer = setTimeout(() => {
        setIsInitialLoading(false);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [callCtx.selectedAgent, callCtx.shouldFetchCalls]);

  // Reset loading state when agent changes
  useEffect(() => {
    setIsInitialLoading(true);
  }, [callCtx.selectedAgent]);

  // Handle agent selection
  const handleAgentChange = event => {
    const agentId = event.target.value;
    const agent = callCtx.availableAgents.find(a => a.id === agentId);

    if (agent) {
      callCtx.setSelectedAgent(agent);
    } else {
      callCtx.setSelectedAgent(null);
    }
  };

  const doCall = async callId => {
    const response = await callCtx.initiateCallback(callId, callCtx.selectedAgent.id);
    if (response.success) {
      toast.success('Anruf ausgelöst.');
      callCtx.setCalls(callCtx.calls.filter(item => item._id !== callId));
    } else if (response.abortType == 'abort-conflict') {
      toast.warn('Rückruf läuft bereits');
      callCtx.setCalls(callCtx.calls.filter(item => item._id !== callId));
    }
  };

  const handleDelete = async (callId, deletedReason) => {
    try {
      const response = await callCtx.deleteCall(callId, deletedReason);
      if (response.success) {
        callCtx.setCalls(callCtx.calls.filter(item => item._id !== callId));
        toast.success('Anruf erfolgreich gelöscht.');
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      toast.error('Fehler beim Löschen des Anrufs.');
    }
  };

  // If no agents are available, show message
  if (callCtx.availableAgents.length === 0) {
    return (
      <Box
        sx={{
          ...viewRootTextPrimarySx,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Card sx={{ maxWidth: 600, width: '100%', mx: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant='h4' component='h1' gutterBottom>
              Keine Comdesk Agents verfügbar
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              Leider ist dir oder deinem Standort aktuell kein Comdesk Agent zugewiesen. Bitte wende
              dich an deinen Standortleiter.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // If no agent is selected and there are multiple agents available, show prominent selection card
  if (!callCtx.selectedAgent && callCtx.availableAgents.length > 1) {
    return (
      <Box
        sx={{
          ...viewRootTextPrimarySx,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Card sx={{ maxWidth: 600, width: '100%', mx: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant='h4' component='h1' gutterBottom>
              Comdesk Agent auswählen
            </Typography>
            <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
              Bitte wähle deinen Comdesk Agent aus, um verfügbare Anrufe anzuzeigen.
            </Typography>
            <FormControl fullWidth sx={{ maxWidth: 400 }}>
              <InputLabel id='agent-select-label'>Comdesk Agent</InputLabel>
              <Select
                labelId='agent-select-label'
                id='agent-select'
                value=''
                label='Comdesk Agent'
                onChange={handleAgentChange}
              >
                {callCtx.availableAgents.map(agent => (
                  <MenuItem key={agent.id} value={agent.id}>
                    <Box>
                      <Typography variant='body1'>{agent.description}</Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {agent.source === 'user' ? 'Eigener Agent' : `${agent.entertainmentName}`}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Show loading state while initially loading calls
  if (isInitialLoading && callCtx.selectedAgent) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          gap: 3,
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant='h5' component='div' color='text.secondary'>
          Lade verfügbare Anrufe...
        </Typography>
      </Box>
    );
  }

  // Agent is selected, show calls table with small agent selector (only if multiple agents available)
  return (
    <Box sx={viewRootTextPrimarySx}>
      {/* Small agent selector in top right - only show if multiple agents available */}
      {callCtx.availableAgents.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <FormControl sx={{ width: { xs: '100%', sm: 250 } }}>
            <InputLabel id='agent-select-small-label'>Comdesk Agent</InputLabel>
            <Select
              labelId='agent-select-small-label'
              id='agent-select-small'
              value={callCtx.selectedAgent.id}
              label='Comdesk Agent'
              onChange={handleAgentChange}
              size='small'
            >
              {callCtx.availableAgents.map(agent => (
                <MenuItem key={agent.id} value={agent.id}>
                  <Box>
                    <Typography variant='body2'>{agent.description}</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {agent.source === 'user' ? 'Eigener Agent' : `${agent.entertainmentName}`}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Calls table */}
      {!!callCtx.calls.length ? (
        <>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 450 }} size='medium' aria-label='a dense table'>
              <TableHead>
                <TableRow>
                  <TableCell>Anrufer</TableCell>
                  {advancedMode ? (
                    <>
                      <TableCell sx={{ width: 100 }} align='center'>
                        Zeitpunkt
                      </TableCell>
                      <TableCell sx={{ width: 100 }} align='center'>
                        Rückrufe
                      </TableCell>
                    </>
                  ) : (
                    ''
                  )}
                  <TableCell sx={{ width: 100 }} align='center'>
                    Status
                  </TableCell>
                  <TableCell sx={{ width: 100 }} align='center'>
                    Aktionen
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {callCtx.calls.map(row => {
                  const callTime = dayjs(row.callbackTime);
                  const now = dayjs();
                  const diffTime = now.diff(callTime, 'minute');

                  const statusBg =
                    diffTime >= 30
                      ? 'error.main'
                      : diffTime >= 15
                        ? 'warning.main'
                        : 'success.main';

                  return (
                    <TableRow
                      key={row._id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component='th' scope='row'>
                        <Box component='span' sx={{ fontWeight: 600, display: 'block' }}>
                          {row.caller}
                        </Box>
                        <br />({row.skillName})
                      </TableCell>
                      {advancedMode ? (
                        <>
                          <TableCell align='center'>
                            {dayjs(row.callFinishedAt).format('DD.MM. HH:mm')}
                          </TableCell>
                          <TableCell align='center'>{row.outgoingCallsLength}</TableCell>
                        </>
                      ) : (
                        ''
                      )}
                      <TableCell align='center'>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Box
                            sx={{
                              width: 15,
                              height: 15,
                              borderRadius: '50%',
                              bgcolor: statusBg,
                              ...(diffTime >= 30
                                ? {
                                    '@keyframes callsStatusDotBlink': {
                                      '0%, 100%': { opacity: 0 },
                                      '50%': { opacity: 1 },
                                    },
                                    animation: 'callsStatusDotBlink 2s infinite',
                                  }
                                : {}),
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align='center'>
                        <Stack direction='row' spacing={1} justifyContent='center'>
                          <Button
                            variant='contained'
                            color='success'
                            size='small'
                            onClick={() => {
                              doCall(row._id);
                            }}
                          >
                            <LockIconWrapper right='call:runcall'>Anrufen</LockIconWrapper>
                          </Button>
                          {canDelete && (
                            <Button
                              variant='contained'
                              color='error'
                              size='small'
                              onClick={() => {
                                setSelectedCallId(row._id);
                                setDeleteModalOpen(true);
                              }}
                            >
                              <LockIconWrapper right='call:delete'>
                                <DeleteIcon />
                              </LockIconWrapper>
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <DeleteCallModal
            open={deleteModalOpen}
            onClose={() => {
              setDeleteModalOpen(false);
              setSelectedCallId(null);
            }}
            onDelete={handleDelete}
            callId={selectedCallId}
          />
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            gap: 3,
          }}
        >
          <PhoneDisabledIcon sx={{ fontSize: 120, color: 'text.secondary', opacity: 0.6 }} />
          <Typography variant='h4' component='div' color='text.secondary' textAlign='center'>
            Keine Anrufe verfügbar
          </Typography>
          <Typography
            variant='body1'
            color='text.secondary'
            textAlign='center'
            sx={{ maxWidth: 400 }}
          >
            Aktuell sind keine Rückrufanfragen für deinen Comdesk Agenten vorhanden. Neue Anrufe
            werden automatisch angezeigt, sobald sie eingehen.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
